import type { CommunityGroup } from '@communities/community-groups/domain';
import { CardGrid } from '@core/components/CardGrid';
import { Card, Text } from '@soteria/design-system-ve';

import { CommunityGroupCard } from './CommunityGroupCard';
import * as styles from './CommunityGroupsGrid.css';

export interface CommunityGroupsGridProps {
  /**
   * List of community groups to display in the grid
   */
  communityGroups: CommunityGroup[];

  /**
   * Function called when a community group card is clicked
   */
  onGroupClick: (groupId: string) => void;

  /**
   * Whether the current user is an admin
   * @default false
   */
  isAdmin?: boolean;

  /**
   * Whether to use auto-fit or auto-fill for grid columns
   * @default "auto-fit"
   */
  columnFit?: 'auto-fit' | 'auto-fill';

  /**
   * Gap between grid items
   * @default "medium"
   */
  gap?: 'small' | 'medium' | 'large';

  /**
   * Custom class name for the empty state message
   */
  emptyStateClassName?: string;

  /**
   * Map of community group IDs to community counts
   * @default {}
   */
  communityCounts?: Record<string, number>;
}

export function CommunityGroupsGrid({
  columnFit = 'auto-fit',
  communityCounts = {},
  communityGroups,
  emptyStateClassName,
  gap = 'medium',
  isAdmin = false,
  onGroupClick,
}: CommunityGroupsGridProps) {
  if (communityGroups.length === 0) {
    return (
      <Card elevation="low" padding="medium">
        <Text className={emptyStateClassName || styles.emptyState}>No community groups found.</Text>
      </Card>
    );
  }

  return (
    <CardGrid columns={columnFit} gap={gap}>
      {communityGroups.map(group => (
        <CommunityGroupCard
          key={group.id}
          communitiesCount={communityCounts[group.id] || 0}
          communityGroup={group}
          isAdmin={isAdmin}
          isSelected={false}
          onClick={() => onGroupClick(group.id)}
        />
      ))}
    </CardGrid>
  );
}
